import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  createFilterOptions,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AppButton,
  FilterBar,
  FilterDrawer,
} from "../../../../../components/ui";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import { UserShortResponse } from "../../../../../api";
import { LeadsService } from "../../../../../api/services/LeadsService";
import {
  buildActiveChips,
  clearFilterField,
  countActiveFilters,
  getCategoryOptions,
  getStageOptions,
  hasActiveFilters,
  resetFilters,
} from "./filtering";
import {
  FILTER_FIELD_LABELS,
  TERMINATION_AGE_OPTIONS,
  type LeadsFiltersState,
} from "./types";

type LeadsTableFiltersProps = {
  filters: LeadsFiltersState;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onApply: (next: LeadsFiltersState) => void;
  onClear: () => void;
  /** Called when creator options finish loading (for chip label resolution). */
  onCreatorsLoaded?: (creators: UserShortResponse[]) => void;
  /** Called when reviewing attorney options finish loading. */
  onReviewingAttorneysLoaded?: (attorneys: UserShortResponse[]) => void;
  /** Hide creator filter (e.g. shared attorney table). */
  hideCreator?: boolean;
  hideSearch?: boolean;
};

const ALL_OPTION = "All";

const stringFilterOptions = createFilterOptions<string>({
  matchFrom: "any",
  stringify: (option) => option,
  trim: true,
  ignoreCase: true,
});

function DateRangeFields({
  label,
  from,
  to,
  onFromChange,
  onToChange,
}: {
  label: string;
  from: Dayjs | null;
  to: Dayjs | null;
  onFromChange: (value: Dayjs | null) => void;
  onToChange: (value: Dayjs | null) => void;
}) {
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1.25}>
        <DatePicker
          label="From"
          value={from}
          onChange={onFromChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="To"
          value={to}
          onChange={onToChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Stack>
    </Box>
  );
}

export function LeadsTableFilters({
  filters,
  searchValue = "",
  onSearchChange,
  onApply,
  onClear,
  onCreatorsLoaded,
  onReviewingAttorneysLoaded,
  hideCreator = false,
  hideSearch = false,
}: LeadsTableFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<LeadsFiltersState>(filters);
  const [creators, setCreators] = useState<UserShortResponse[]>([]);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const [reviewingAttorneys, setReviewingAttorneys] = useState<UserShortResponse[]>([]);
  const [reviewingAttorneysLoading, setReviewingAttorneysLoading] = useState(false);
  const filtersActive = hasActiveFilters(filters);
  const draftActive = hasActiveFilters(draft);
  const activeCount = countActiveFilters(filters);
  const activeChips = buildActiveChips(filters);
  const categoryOptions = useMemo(() => getCategoryOptions(), []);
  const stageOptions = useMemo(() => getStageOptions(), []);

  useEffect(() => {
    if (hideCreator) {
      return;
    }
    let cancelled = false;
    setCreatorsLoading(true);
    LeadsService.getApiLeadsCreators()
      .then((users) => {
        if (cancelled) return;
        const list = users ?? [];
        setCreators(list);
        onCreatorsLoaded?.(list);
      })
      .catch(() => {
        if (!cancelled) {
          setCreators([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCreatorsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hideCreator, onCreatorsLoaded]);

  useEffect(() => {
    let cancelled = false;
    setReviewingAttorneysLoading(true);
    LeadsService.getApiLeadsReviewingAttorneys()
      .then((users) => {
        if (cancelled) return;
        const list = users ?? [];
        setReviewingAttorneys(list);
        onReviewingAttorneysLoaded?.(list);
      })
      .catch(() => {
        if (!cancelled) {
          setReviewingAttorneys([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReviewingAttorneysLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onReviewingAttorneysLoaded]);

  const creatorOptions = useMemo(() => {
    return creators
      .map((c) => ({
        id: String(c.id ?? ""),
        label: `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || `User ${c.id}`,
      }))
      .filter((c) => c.id)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [creators]);

  const reviewingAttorneyOptions = useMemo(() => {
    return reviewingAttorneys
      .map((a) => ({
        id: String(a.id ?? ""),
        label: `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim() || `User ${a.id}`,
      }))
      .filter((a) => a.id)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [reviewingAttorneys]);

  useEffect(() => {
    if (drawerOpen) {
      setDraft(filters);
    }
  }, [drawerOpen, filters]);

  const patchDraft = (patch: Partial<LeadsFiltersState>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const clearField = (key: keyof LeadsFiltersState) => {
    onApply(clearFilterField(filters, key));
  };

  const handleApply = () => {
    onApply(draft);
    setDrawerOpen(false);
  };

  const clearDraft = () => setDraft(resetFilters());

  const handleClearAll = () => {
    onClear();
    onSearchChange?.("");
  };

  return (
    <>
      <FilterBar
        activeCount={activeCount}
        filtersActive={filtersActive}
        onOpenFilters={() => setDrawerOpen(true)}
        onClear={handleClearAll}
        clearDisabled={!filtersActive && !searchValue}
        chips={activeChips.map((chip) => ({
          key: String(chip.key),
          label: chip.label,
          onDelete: () => clearField(chip.key),
        }))}
        leading={
          !hideSearch ? (
            <TextField
              size="small"
              placeholder="Search leads…"
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 260 },
                bgcolor: "background.paper",
                borderRadius: 1,
              }}
            />
          ) : undefined
        }
      />

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        subtitle="Apply to refresh the leads table with these filters"
        activeCount={activeCount}
        actions={
          <>
            <AppButton
              variant="outlined"
              color="inherit"
              fullWidth
              startIcon={<FilterAltOffIcon />}
              onClick={clearDraft}
              disabled={!draftActive}
            >
              Clear filters
            </AppButton>
            <AppButton variant="contained" fullWidth onClick={handleApply}>
              Apply
            </AppButton>
          </>
        }
      >
          <Stack spacing={1.75}>
            <Autocomplete
              size="small"
              options={[ALL_OPTION, ...categoryOptions.map((o) => o.label)]}
              value={
                draft.category
                  ? categoryOptions.find((o) => o.value === draft.category)?.label ??
                    ALL_OPTION
                  : ALL_OPTION
              }
              onChange={(_, next) => {
                if (!next || next === ALL_OPTION) {
                  patchDraft({ category: "" });
                  return;
                }
                const match = categoryOptions.find((o) => o.label === next);
                patchDraft({ category: match?.value ?? "" });
              }}
              filterOptions={stringFilterOptions}
              autoHighlight
              clearOnBlur={false}
              handleHomeEndKeys
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={FILTER_FIELD_LABELS.category}
                  placeholder="Type to filter…"
                />
              )}
              fullWidth
            />

            <Autocomplete
              size="small"
              options={[ALL_OPTION, ...stageOptions.map((o) => o.label)]}
              value={
                draft.stage
                  ? stageOptions.find((o) => o.value === draft.stage)?.label ??
                    ALL_OPTION
                  : ALL_OPTION
              }
              onChange={(_, next) => {
                if (!next || next === ALL_OPTION) {
                  patchDraft({ stage: "" });
                  return;
                }
                const match = stageOptions.find((o) => o.label === next);
                patchDraft({ stage: match?.value ?? "" });
              }}
              filterOptions={stringFilterOptions}
              autoHighlight
              clearOnBlur={false}
              handleHomeEndKeys
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={FILTER_FIELD_LABELS.stage}
                  placeholder="Type to filter…"
                />
              )}
              fullWidth
            />

            {!hideCreator && (
              <Autocomplete
                size="small"
                loading={creatorsLoading}
                options={[
                  { id: "", label: ALL_OPTION },
                  ...creatorOptions,
                ]}
                value={
                  creatorOptions.find((o) => o.id === draft.createdById) ?? {
                    id: "",
                    label: ALL_OPTION,
                  }
                }
                onChange={(_, next) => {
                  if (!next || !next.id) {
                    patchDraft({ createdById: "", createdByName: "" });
                    return;
                  }
                  patchDraft({
                    createdById: next.id,
                    createdByName: next.label,
                  });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                autoHighlight
                clearOnBlur={false}
                handleHomeEndKeys
                ListboxProps={{ style: { maxHeight: 280 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={FILTER_FIELD_LABELS.createdById}
                    placeholder={
                      creatorsLoading ? "Loading creators…" : "Type to filter…"
                    }
                  />
                )}
                fullWidth
              />
            )}

            <Autocomplete
              size="small"
              loading={reviewingAttorneysLoading}
              options={[
                { id: "", label: ALL_OPTION },
                ...reviewingAttorneyOptions,
              ]}
              value={
                reviewingAttorneyOptions.find(
                  (o) => o.id === draft.reviewingAttorneyId
                ) ?? {
                  id: "",
                  label: ALL_OPTION,
                }
              }
              onChange={(_, next) => {
                if (!next || !next.id) {
                  patchDraft({
                    reviewingAttorneyId: "",
                    reviewingAttorneyName: "",
                  });
                  return;
                }
                patchDraft({
                  reviewingAttorneyId: next.id,
                  reviewingAttorneyName: next.label,
                });
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              autoHighlight
              clearOnBlur={false}
              handleHomeEndKeys
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={FILTER_FIELD_LABELS.reviewingAttorneyId}
                  placeholder={
                    reviewingAttorneysLoading
                      ? "Loading attorneys…"
                      : "Type to filter…"
                  }
                />
              )}
              fullWidth
            />

            <DateRangeFields
              label="Created At"
              from={draft.createdFrom}
              to={draft.createdTo}
              onFromChange={(value) => patchDraft({ createdFrom: value })}
              onToChange={(value) => patchDraft({ createdTo: value })}
            />

            <Autocomplete
              size="small"
              options={[
                { value: "", label: ALL_OPTION },
                ...TERMINATION_AGE_OPTIONS,
              ]}
              value={
                TERMINATION_AGE_OPTIONS.find(
                  (o) => o.value === draft.terminationAge
                ) ?? { value: "", label: ALL_OPTION }
              }
              onChange={(_, next) =>
                patchDraft({
                  terminationAge: !next || !next.value ? "" : next.value,
                })
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              autoHighlight
              clearOnBlur={false}
              handleHomeEndKeys
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={FILTER_FIELD_LABELS.terminationAge}
                  placeholder="Type to filter…"
                  helperText="Based on employment / workers’ comp termination date"
                />
              )}
              fullWidth
            />
          </Stack>
      </FilterDrawer>
    </>
  );
}
